import { BsdsCheckbox, BsdsModal } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';

import type { DrfPortalTermDefinition, DrfTermContentState } from '../../../../utils/drfTermsUtil';
import { formatDrefVigenciaDisplay, normalizeDrfTermHeadingTags } from '../../../../utils/drfTermsUtil';
import type { TabItem } from '../../../../types/componentType';
import Button from '../../../button/Button';
import Loading from '../../../loading/Loading';
import SystemMessage from '../../../systemMessage/SystemMessage';
import Tab from '../../../tabs/Tab';

type DrfTermUserModalProps = {
  isOpen: boolean;
  userName: string;
  portalTermDefinitions: readonly DrfPortalTermDefinition[];
  termContentByTab: Record<string, DrfTermContentState>;
  allTabsViewed: boolean;
  onTabSelected: (tabId: string) => void;
  onSave: () => Promise<void>;
  saveLoading: boolean;
  saveError: string | null;
  onRetrySave: () => void;
};

function renderTermTabPanel(state: DrfTermContentState) {
  if (state.status === 'loading') {
    return (
      <div >
        <Loading />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="rounded-md p-4 mb-40">
        <p className="text-xs mt-4 text-center text-gray-500">{state.message}</p>
      </div>
    );
  }

  if (state.status !== 'success') {
    return null;
  }

  const { content } = state;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <h6 className="text-sm font-semibold text-gray-900">{content.title}</h6>
      <p className="text-xs text-gray-500 bsds-mt-4">
        Última atualização: {formatDrefVigenciaDisplay(content.updatedAt)}
      </p>
      <div
        className="max-w-none text-xs text-gray-700 bsds-mt-12 leading-relaxed [&_h6]:text-sm [&_h6]:font-semibold [&_h6]:text-gray-900 [&_h6]:my-2 [&_p]:my-2"
        dangerouslySetInnerHTML={{ __html: normalizeDrfTermHeadingTags(content.description) }}
      />
    </div>
  );
}

const DrfTermUserModal: React.FC<DrfTermUserModalProps> = ({
  isOpen,
  userName,
  portalTermDefinitions,
  termContentByTab,
  allTabsViewed,
  onTabSelected,
  onSave,
  saveLoading,
  saveError,
  onRetrySave,
}) => {
  const modalId = useId();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [hintAttention, setHintAttention] = useState(false);
  const [hintShakeKey, setHintShakeKey] = useState(0);

  const canToggleAcceptance = allTabsViewed && !saveLoading;

  const hasLoadError = useMemo(() => {
    if (saveError) {
      return true;
    }
    return Object.values(termContentByTab).some((state) => state.status === 'error');
  }, [saveError, termContentByTab]);

  useEffect(() => {
    if (isOpen) {
      setAcceptedTerms(false);
      setHintAttention(false);
      setHintShakeKey(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (allTabsViewed) {
      setHintAttention(false);
    }
  }, [allTabsViewed]);

  const triggerHintAttention = useCallback(() => {
    if (!canToggleAcceptance && !allTabsViewed) {
      setHintAttention(true);
      setHintShakeKey((value) => value + 1);
    }
  }, [allTabsViewed, canToggleAcceptance]);

  const handleAcceptanceLabelClick = useCallback(
    (event: React.MouseEvent<HTMLLabelElement>) => {
      if (!canToggleAcceptance) {
        triggerHintAttention();
        event.preventDefault();
      }
    },
    [canToggleAcceptance, triggerHintAttention],
  );

  const handleAcceptanceLabelKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLLabelElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      if (!canToggleAcceptance) {
        triggerHintAttention();
        event.preventDefault();
      }
    },
    [canToggleAcceptance, triggerHintAttention],
  );

  const tabs = useMemo<TabItem[]>(
    () =>
      portalTermDefinitions.map((term) => ({
        id: term.id,
        title: term.title,
        icon: <span aria-hidden="true" className="w-4 h-4" />,
        content: renderTermTabPanel(termContentByTab[term.id] ?? { status: 'idle' }),
      })),
    [portalTermDefinitions, termContentByTab],
  );

  const handleActiveTabChange = useCallback(
    (tab: TabItem) => {
      onTabSelected(tab.id);
    },
    [onTabSelected],
  );

  const handleSave = () => {
    void onSave();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <BsdsModal
      isOpen={isOpen}
      backdropDismiss={false}
      color="secondary"
      canDismiss={false}
      animated
      showBackdrop
      keepContentsMounted
      platform="web"
      aria-labelledby={modalId}
      aria-describedby={`${modalId}-description`}
    >
      <div
        className={`modal-content flex flex-col h-screen max-h-screen  ${hasLoadError ? 'md:h-[65vh]' : 'md:h-[55vh]'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalId}
      >
        <div className="relative bg-red-700 p-4 pb-2 text-white shrink-0">
          <h5 id={modalId} className="text-md font-semibold pr-8">
            Ativação de Acesso ao Portal do Estipulante
          </h5>
          <h6 className="text-xs text-red-100 bsds-mt-4">Bem-vindo(a), {userName}</h6>
        </div>

        <div
          id={`${modalId}-description`}
          className="overflow-y-auto custom-scrollbar flex-1 p-4 min-h-0 space-y-4"
          role="region"
          aria-label="Termos de uso do portal"
        >
          <div className="rounded-md border border-blue-100 bg-blue-50 p-2">
            <h6 className="text-sm text-blue-700">
              Para ativar seu perfil, é necessário aceitar os Termos de Uso abaixo.
            </h6>
          </div>

          <Tab
            tabs={tabs}
            defaultTabId={portalTermDefinitions[0]?.id}
            onActiveTabChange={handleActiveTabChange}
          />
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-white p-4 space-y-3">
          {hasLoadError && saveError && (
            <div role="alert">
              <SystemMessage message={saveError} type="error" />
            </div>
          )}

          {!hasLoadError && (
            <>
              {!allTabsViewed && (
                <h6
                  key={hintShakeKey}
                  className={`text-xs ${hintAttention ? 'text-red-600 animate-drf-hint-shake' : 'text-gray-500'
                    }`}
                >
                  Leia o conteúdo das 3 abas para continuar.
                </h6>
              )}
              <label
                className={`flex items-start gap-3 rounded-md border p-3 ${canToggleAcceptance ? 'border-gray-200' : 'border-gray-100 bg-gray-50'
                  } ${!canToggleAcceptance && !allTabsViewed ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                onMouseEnter={triggerHintAttention}
                onClick={handleAcceptanceLabelClick}
                onKeyDown={handleAcceptanceLabelKeyDown}
              >
                <BsdsCheckbox
                  checked={acceptedTerms}
                  onBsdsChange={() => {
                    if (canToggleAcceptance) {
                      setAcceptedTerms((value) => !value);
                    }
                  }}
                  color="primary"
                  disabled={!canToggleAcceptance}
                  aria-label="Aceito os Termos de Uso e o tratamento de dados"
                />
                <span className={`text-xs ${canToggleAcceptance ? 'text-gray-700' : 'text-gray-400'}`}>
                  Declaro que li e aceito o Termo de Uso, o Termo de Adesão e o Aviso de Privacidade, estando ciente das condições de uso do Portal e do tratamento dos meus dados pessoais, conforme descrito nesses documentos.
                </span>
              </label>
            </>
          )}

          {hasLoadError ? (
            <Button
              type="button"
              color="primary"
              fill="outline"
              onClick={onRetrySave}
              disabled={saveLoading}
              loading={saveLoading}
              className="w-full"
              aria-label="Tentar carregar novamente"
            >
              Tentar novamente
            </Button>
          ) : (
            <Button
              type="button"
              color="primary"
              fill="outline"
              onClick={handleSave}
              disabled={!acceptedTerms || saveLoading}
              loading={saveLoading}
              className="w-full"
              aria-label="Salvar aceite dos termos"
            >
              Salvar
            </Button>
          )}
        </div>
      </div>
    </BsdsModal>
  );
};

export default DrfTermUserModal;
